/**
 * Folders are first-class entities that organise projects within an org. A
 * folder has a stable id and an optional `parentFolderId` (null = org root),
 * forming an adjacency-list tree. A project references at most one folder via
 * `folderId` (null = org root, "unfiled").
 *
 * Folders are organisational — they grant nothing and carry no billing
 * semantics. Access is still controlled at the org/project level, and a folder
 * is visible only to folder managers and to people who can view a project
 * inside it; folder-level grants are a later step (see ADR-0015 D3).
 */

/** Separator used when composing a folder's ancestor names into a display path. */
export const FOLDER_PATH_SEPARATOR = '/'

/** Maximum length of a single folder name (matches the `name` column). */
export const FOLDER_NAME_MAX_LENGTH = 255

/**
 * Maximum folder depth, counted as levels between the org root and a project.
 * A top-level folder is depth 1; the deepest allowed folder is depth 8.
 *
 * Enforced in application code (not the schema), so raising it later needs no
 * data migration.
 */
export const FOLDER_MAX_DEPTH = 8

/** Per-org cap on the number of folders. */
export const FOLDER_MAX_PER_ORG = 1000

/** Validation message for a name that is empty or contains the separator. */
export const FOLDER_NAME_ERROR =
  "A folder name is required and can't contain '/'"

/** Validation message for exceeding {@link FOLDER_MAX_DEPTH}. */
export const FOLDER_DEPTH_ERROR = `Folders can't nest deeper than ${FOLDER_MAX_DEPTH} levels`

/** Validation message for moving a folder into itself or one of its descendants. */
export const FOLDER_CYCLE_ERROR =
  "A folder can't be moved into itself or one of its descendants"

/** Validation message for a duplicate sibling name. */
export const FOLDER_NAME_CONFLICT_ERROR =
  'A folder with this name already exists here'

/** Validation message for deleting a non-empty folder. */
export const FOLDER_NOT_EMPTY_ERROR =
  'A folder must be empty before it can be deleted'

/** Validation message for exceeding {@link FOLDER_MAX_PER_ORG}. */
export const FOLDER_MAX_PER_ORG_ERROR = `An organisation can't have more than ${FOLDER_MAX_PER_ORG} folders`

/** Canonicalise a raw folder name: trim surrounding whitespace. */
export const normalizeFolderName = (name: string): string => name.trim()

/**
 * True when a folder name is valid: non-empty after trimming, within the length
 * bound, and containing no path separator (folder names are single segments).
 */
export const isValidFolderName = (name: string): boolean => {
  const normalized = normalizeFolderName(name)
  return (
    normalized.length > 0 &&
    normalized.length <= FOLDER_NAME_MAX_LENGTH &&
    !normalized.includes(FOLDER_PATH_SEPARATOR)
  )
}

/** Compose an ordered list of ancestor names into a display path. */
export const folderDisplayPath = (names: string[]): string =>
  names.join(FOLDER_PATH_SEPARATOR)

/* -------------------------------- Entities -------------------------------- */

export type Folder = {
  id: string

  /** The id of the org that this folder belongs to. */
  orgId: string

  /** Parent folder id, or `null` when the folder sits at the org root. */
  parentFolderId: string | null

  /** Single-segment folder name (no `/`). */
  name: string

  created: Date
  createdBy: string
  updated: Date
  updatedBy: string
}

/** Lightweight folder reference used for breadcrumb / ancestor chains. */
export type FolderRef = Pick<Folder, 'id' | 'name' | 'parentFolderId'>

/**
 * How many direct children are sampled per folder for the content peek
 * (the 2×2 preview grid on a folder tile).
 */
export const FOLDER_PREVIEW_LIMIT = 4

/** A folder's direct child, sampled for the content-peek preview. */
export type FolderPreviewItem = {
  type: 'folder' | 'project'
  id: string
  name: string
}

/**
 * A folder plus the aggregates that drive its tile: the subtree project count
 * and the direct sub-folder count (the count line), the direct project count
 * (the +N overflow chip) and a name-ordered sample of direct children (the
 * peek).
 */
export type FolderSummary = Folder & {
  /** Projects at or below this folder — its whole subtree. */
  projectCount: number

  /** Sub-folders directly inside this folder (one level only). */
  subfolderCount: number

  /** Projects filed directly in this folder (excludes sub-folder contents). */
  directProjectCount: number

  /**
   * The first {@link FOLDER_PREVIEW_LIMIT} direct children (sub-folders and
   * projects together), ordered case-insensitively by name. The peek does not
   * recurse into sub-folders — they render as glyphs (ADR-0015 brief §6).
   */
  preview: FolderPreviewItem[]
}

/* --------------------------------- Inputs --------------------------------- */

export type CreateFolderInput = {
  orgId: string
  name: string
  /** Parent folder to create under; omit / `null` creates at the org root. */
  parentFolderId?: string | null
}

export type ListFoldersInput = {
  orgId: string
  /** List the immediate children of this folder; omit / `null` lists the root. */
  parentFolderId?: string | null
}

export type ListFoldersResult = {
  /**
   * Immediate child folders of the requested parent. Callers needing the
   * parent's ancestor chain (e.g. a breadcrumb) derive it client-side from
   * {@link ListFolderTreeResult}, which they already fetch for navigation.
   */
  folders: FolderSummary[]
}

export type ListFolderTreeInput = {
  orgId: string
}

export type ListFolderTreeResult = {
  /**
   * Every folder in the org as a flat list (up to {@link FOLDER_MAX_PER_ORG}),
   * for building the whole tree client-side — e.g. a move-destination picker.
   * Ordered by name; assemble the tree via `parentFolderId`.
   */
  folders: FolderRef[]
}

export type RenameFolderInput = {
  folderId: string
  name: string
}

export type MoveFolderInput = {
  folderId: string
  /** New parent folder; `null` moves the folder to the org root. */
  parentFolderId: string | null
}

export type DeleteFolderInput = {
  folderId: string
}
